import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SplitService } from './helpers/split.service';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private featureFlags = new BehaviorSubject<{[key: string]: boolean}>({
    newChartImplementation: (environment as any).features?.newChartImplementation || false,
    twoDimensionalChartingAction: (environment as any).features?.twoDimensionalChartingAction || false,
  });

  constructor(private splitService: SplitService) {
    // Load feature flags from Split.io
    this.loadFeatureFlags();
  }

  private loadFeatureFlags(): void {
    // Load newChartImplementation from Split.io
    this.splitService.getTreatment('new_chart_implementation')
      .then(treatment => {
        const isEnabled = treatment === 'on';
        this.featureFlags.next({ 
          ...this.featureFlags.value, 
          newChartImplementation: isEnabled 
        });
      })
      .catch(error => {
        // Fallback to environment value
        this.featureFlags.next({ 
          ...this.featureFlags.value, 
          newChartImplementation: (environment as any).features?.newChartImplementation || false 
        });
      });

    // Keeps the sync getter in sync with Split (used by audit/print views).
    this.splitService.getTreatmentObservable('two_dimensional_charting_action')
      .subscribe({
        next: treatment => {
          this.featureFlags.next({
            ...this.featureFlags.value,
            twoDimensionalChartingAction: treatment === 'on'
          });
        },
        error: () => {
          this.featureFlags.next({
            ...this.featureFlags.value,
            twoDimensionalChartingAction: (environment as any).features?.twoDimensionalChartingAction || false
          });
        }
      });
  }

  public getFeatureFlag(flagName: string): boolean {
    return this.featureFlags.value[flagName] || false;
  }

  public getFeatureFlags(): Observable<{[key: string]: boolean}> {
    return this.featureFlags.asObservable();
  }

  // Method to check if new chart implementation should be used
  public useNewChartImplementation(): boolean {
    return this.getFeatureFlag('newChartImplementation');
  }

  public useTwoDimensionalChartingAction(): boolean {
    return this.getFeatureFlag('twoDimensionalChartingAction');
  }

  // Reactive 2D charting flag. Binds to Split at subscribe time so it stays live
  // across client re-init (SplitService.updateKey) without a page refresh.
  public twoDimensionalChartingAction$(): Observable<boolean> {
    return this.splitService.getTreatmentObservable('two_dimensional_charting_action').pipe(
      map(treatment => treatment === 'on'),
      distinctUntilChanged()
    );
  }

  // Same stream; named for guards that navigate away, where only a resolved value is safe.
  public twoDimensionalChartingActionResolved$(): Observable<boolean> {
    return this.twoDimensionalChartingAction$();
  }

  // Method to refresh feature flags from Split.io
  public refreshFeatureFlags(): void {
    this.loadFeatureFlags();
  }
}
